import React, { useMemo } from "react";
import {
	LineChart,
	Line,
	XAxis,
	YAxis,
	CartesianGrid,
	Tooltip,
	ResponsiveContainer,
} from "recharts";
import { Checkbox, FormControlLabel, Grid } from "@mui/material";
import { DataItem } from "../../types";
import iwanthue from "iwanthue";
import { CustomTooltip, formatDateDDMMYYYY } from "../utilization/utils";

interface LineChartProps {
	data: DataItem[];
	YAxisLabel: string;
	selectedEmployees?: string[];
	onEmployeeSelection?: (employeeId: string) => void;
	hideWeeklyAverage?: boolean;
}

const colorMapCache: { key: string; map: Record<string, string> } = {
	key: "",
	map: {},
};

function getColorMap(
	employeeNames: { name: string; id: string }[]
): Record<string, string> {
	const namesAsStrings = employeeNames.map((e) => e.name);
	const cacheKey = namesAsStrings.join("|");

	if (colorMapCache.key === cacheKey) {
		return colorMapCache.map;
	}

	const colorCount = Math.max(namesAsStrings.length, 2);
	const colors = iwanthue(colorCount, {
		colorSpace: "all",
		clustering: "force-vector",
		seed: "employee-colors",
		quality: 50,
	});

	const colorMap: Record<string, string> = {};
	namesAsStrings.forEach((name, index) => {
		colorMap[name] = colors[index];
	});

	colorMapCache.key = cacheKey;
	colorMapCache.map = colorMap;
	return colorMap;
}

function transformData(
	data: DataItem[],
	selectedEmployees?: string[]
): any[] {
	const filteredData = data.filter((item) =>
		selectedEmployees?.includes(item.employee_name)
	);

	const weekMap = new Map<string, any>();
	filteredData.forEach((item) => {
		let weekData = weekMap.get(item.week);
		if (!weekData) {
			weekData = { week: item.week };
			weekMap.set(item.week, weekData);
		}
		weekData[item.employee_name] = item.percent;
		const br = item.project_breakdown;
		if (Array.isArray(br) && br.length > 0) {
			if (!weekData.__utilizationTooltip) {
				weekData.__utilizationTooltip = {};
			}
			weekData.__utilizationTooltip[item.employee_name] = br;
		}
	});

	return Array.from(weekMap.values());
}

const LineChartComponent: React.FC<LineChartProps> = ({
	data = [],
	YAxisLabel,
	selectedEmployees,
	onEmployeeSelection,
	hideWeeklyAverage = false,
}) => {
	const employeeNames = useMemo(() => {
		return Array.from(
			data
				.reduce((acc, item) => {
					if (!acc.has(item.employee_name)) {
						acc.set(item.employee_name, {
							name: item.employee_name,
							id: item.employee_id,
						});
					}
					return acc;
				}, new Map<string, { name: string; id: string }>())
				.values()
		);
	}, [data]);

	const employeeColorMap = useMemo(
		() => getColorMap(employeeNames),
		[employeeNames]
	);
	const transformedData = useMemo(
		() => transformData(data, selectedEmployees),
		[data, selectedEmployees]
	);

	const hasManyPoints = transformedData.length > 10;

	return (
		<>
			<ResponsiveContainer width="100%" aspect={3}>
				<LineChart
					data={transformedData}
					margin={{ top: 20, right: 30, bottom: 60, left: 56 }}
				>
					<CartesianGrid strokeDasharray="3 3" />
					<XAxis
						dataKey="week"
						tickFormatter={formatDateDDMMYYYY}
						angle={-45}
						textAnchor="end"
						height={70}
						interval="preserveStartEnd"
						tick={{ fontSize: 11 }}
					/>
					<YAxis
						label={{
							value: YAxisLabel,
							angle: -90,
							position: "insideLeft",
							offset: 8,
							style: { textAnchor: "middle" },
						}}
						tick={{ fontSize: 12 }}
					/>
					<Tooltip
						cursor={{ fill: "transparent" }}
						content={<CustomTooltip hideWeeklyAverage={hideWeeklyAverage} />}
						wrapperStyle={{ zIndex: 9999, pointerEvents: "auto" }}
					/>
					{selectedEmployees?.map((employeeName) => (
						<Line
							key={employeeName}
							type="monotone"
							dataKey={employeeName}
							stroke={employeeColorMap[employeeName]}
							strokeWidth={2}
							dot={!hasManyPoints}
							activeDot={{ r: 5 }}
							animationDuration={300}
							animationEasing="ease-in-out"
						/>
					))}
				</LineChart>
			</ResponsiveContainer>
			<Grid
				container
				display="flex"
				alignItems="start"
				justifyContent="center"
				sx={{ mb: 4, mt: 2 }}
			>
				{employeeNames.map((employee, index) => (
					<FormControlLabel
						key={index}
						control={
							<Checkbox
								checked={selectedEmployees?.includes(employee?.name)}
								onChange={() => {
									if (onEmployeeSelection) {
										onEmployeeSelection(employee?.name);
									}
								}}
							/>
						}
						label={employee?.name}
						sx={{
							color: employeeColorMap[employee?.name],
						}}
					/>
				))}
			</Grid>
		</>
	);
};

export default LineChartComponent;
